import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  CircularProgress,
  styled,
  Typography,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useSearchAndSort } from "../../hooks/useSearchAndSort";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAllLoanApplications, getApplicationDetails } from "../../API/API";
import LoanApplicationPDF from "../apply-loan/LoanApplicationPDF";
import PreviewApplication from "../apply-loan/PreviewApplication";
import CentralizedTable from "../../components/common/CentralizedTable/CentralizedTable";
import { borrowerApplicationColumns } from "../../components/common/CentralizedTable/ColumnConfigs";
import { setFiles, setFormData, setResumed } from "../../ReduxStore/UserSlice";
import { toast } from "react-toastify";

const PageWrapper = styled(Box)({
  backgroundColor: "#F5F6F7",
  minHeight: "100vh",
  padding: 0,
  margin: 0,
  display: "flex",
  justifyContent: "center",
});

const ContentContainer = styled(Box)({
  backgroundColor: "#FFFFFF",
  width: "100%",
  maxWidth: "1200px",
  padding: "24px 32px",
  boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.05)",
  border: "1px solid #E5E8EB",
  margin: "24px 16px",
  borderRadius: "10px",
});

const ApplyLoanButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#0052CC",
  color: "white",
  borderRadius: "8px",
  padding: "12px 24px",
  textTransform: "none",
  fontFamily: "'Inter', sans-serif",
  fontSize: "16px",
  fontWeight: 600,
  boxShadow: "none",
  width: "200px",
  margin: "20px auto 0",
  "&:hover": {
    backgroundColor: "#003E99",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
  },
}));

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "48px 24px",
  textAlign: "center",
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color = "#4CAF50";
  let backgroundColor = "#E8F5E9";

  if (status === "Pending" || status === "pending" || status === "incomplete") {
    color = "#FFA000";
    backgroundColor = "#FFF8E1";
  } else if (status === "Rejected" || status === "rejected") {
    color = "#F44336";
    backgroundColor = "#FFEBEE";
  } else if (status === "Processing" || status === "processing") {
    color = "#2196F3";
    backgroundColor = "#E3F2FD";
  } else if (status === "Submitted" || status === "submitted") {
    color = "#4CAF50";
    backgroundColor = "#E8F5E9";
  }

  return {
    color: color,
    backgroundColor: backgroundColor,
    fontWeight: 600,
    borderRadius: "16px",
  };
});

const ApplicationHistory = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const userFromRedux = useSelector((state) => state.user.user);
  const dispatch = useDispatch();
  const { searchQuery, setSearchQuery, handleSort, searchAndSortParams, sortBy, order } = useSearchAndSort(500, 'application-history');

  useEffect(() => {
    const presetQuery = localStorage.getItem("applicationHistorySearchQuery");
    if (presetQuery) {
      setSearchQuery(presetQuery);
      localStorage.removeItem("applicationHistorySearchQuery");
    }
  }, [setSearchQuery]);

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        setLoading(true);

        const userId =
          userFromRedux?.ID ||
          userFromRedux?.id ||
          (localStorage.getItem("user")
            ? JSON.parse(localStorage.getItem("user"))?.ID
            : null);

        const fieldMap = {
          'applicationNumber': 'applicationNumber',
          'createDatetime': 'createDatetime',
          'endUseOfFund': 'endUseOfFund',
          'fundRequirement': 'fundRequirement',
          'status': 'status'
        };

        const apiSortBy = searchAndSortParams.sortBy 
          ? (fieldMap[searchAndSortParams.sortBy] || searchAndSortParams.sortBy)
          : undefined;

        const response = await getAllLoanApplications(null, userId, {
          searchQuery: searchAndSortParams.searchQuery,
          sortBy: apiSortBy,
          order: searchAndSortParams.order
        });

        if (response && response?.statusCode) {
          setApplications(response?.result?.data);
        } else {
          setApplications([]);
        }
        setError(null);
      } catch (err) {
        console.error("Error fetching applications:", err);
        setError("Failed to load application history. Please try again later.");
        setApplications([]);
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, [userFromRedux, searchAndSortParams.searchQuery, searchAndSortParams.sortBy, searchAndSortParams.order]);

  const handleTrackApplication = (application) => {
  navigate(`/track-application?applicationId=${application?.applicationId}`, {
    state: {
      number: application?.applicationNumber,
      submittedAt:application?.createDatetime                   
    }
  });
};

  const pdfRefs = useRef({});

  const handleDownload = (applicationId) => {
    if (pdfRefs.current[applicationId]) {
      pdfRefs.current[applicationId].downloadPDF();
    }
  };

  const handleApplyLoan = () => {
    navigate("/apply-loan");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownloadDocument = (url, filename) => {
    if (url) {
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = filename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };

  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  
  const handleViewApplication = async (application) => {
    try {
      setLoading(true);
      const applicationId = application?.applicationId;
      if (!applicationId) {
        setSelectedApplication({ ...application });
        setPreviewOpen(true);
        return;
      }
      const response = await getApplicationDetails(applicationId);
      const applicationData = response?.result;
      if (applicationData) {
        const filesObj = {};
        const documentTypeMapping = {
          BorrowerPanCard: "borrowerPanCard",
          CompanyPAN: "borrowerPanCard",
          MOA: "moa",
          AOA: "aoa",
          Shareholding: "shareholding",
          IncorporationDateProof: "incorporationDateProof",
          AadharCard: "AadharCard",
          PanCard: "panCard",
          BankStatement: "bankStatement",
          SLLoan: "slLoan",
          DebitProfile: "debitProfile",
          CoLetteral: "coLetteral",
          OrderCopy: "orderCopy",
          ContractCopy: "contractCopy",
          Certification: "certification",
          CreditRating: "creditRating",
          Other: "other",
          BalanceSheet: "auditedBalanceFYPrev1",
        };
        if (Array.isArray(applicationData.documents)) {
          applicationData.documents.forEach((doc) => {
            const mappedKey = documentTypeMapping[doc.documentType] || doc.documentType?.charAt(0).toLowerCase() + doc.documentType?.slice(1);
            if (!mappedKey) return;
            if (["bankStatement", "slLoan"].includes(mappedKey)) {
              if (!Array.isArray(filesObj[mappedKey])) filesObj[mappedKey] = [];
              filesObj[mappedKey].push({ url: doc.documentPath, filename: doc.documentName, remote: true });
            } else {
              filesObj[mappedKey] = { url: doc.documentPath, filename: doc.documentName, remote: true };
            }
          });
        }
        setSelectedApplication({ ...application, ...applicationData, loanDoc: filesObj });
      } else {
        setSelectedApplication({ ...application });
      }
      setPreviewOpen(true);
    } catch (err) {
      toast.error("Error fetching application details for preview:", err);
      setSelectedApplication({ ...application });
      setPreviewOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleViewOffer = (offer) => {
    const offerId = offer?.id || offer?.offerId;
    if (offerId) {
      navigate(`/proposal-details/${offerId}`);
    }
  };

  const handleEditApplication = async (application) => {
    try {
      setLoading(true);
      const applicationId = application?.applicationId;
      if (!applicationId) return;
      const response = await getApplicationDetails(applicationId);
      const applicationData = response?.result;
      if (applicationData) {
        const sanitizedData = {};
        Object.entries(applicationData).forEach(([key, value]) => {
          if (value === null || value === undefined) {
            sanitizedData[key] = "";
            return;
          }
          if (typeof value === "object" && !Array.isArray(value)) {
            if (key === "userId" || key === "documents") {
              sanitizedData[key] = value;
            } else {
              sanitizedData[key] = JSON.stringify(value);
            }
          } else if (Array.isArray(value)) {
            sanitizedData[key] = value;
          } else if (typeof value === "number") {
            sanitizedData[key] = value;
          } else if (typeof value === "boolean") {
            sanitizedData[key] = value;
          } else {
            sanitizedData[key] = String(value);
          }
        });
        sanitizedData.applicationId = applicationId;

        if (sanitizedData.anticipatedProjectGrowthRateForCurrentYear != null) {
          sanitizedData.anticipatedGrowthRateCurrentYear =
            sanitizedData.anticipatedProjectGrowthRateForCurrentYear;
        }

        if (applicationData?.mcaDocxStatus || applicationData?.dataFetchedFromPortal) {
          sanitizedData.datafetchedFromportal = true;
          if (applicationData.mcaDocxStatus != null) {
            sanitizedData.mcaDocxStatus = applicationData.mcaDocxStatus;
          }
        }

        dispatch(setFormData(sanitizedData));

        if (applicationData.documents && applicationData.documents.length > 0) {
          const filesObj = {};
          const documentTypeMapping = {
            BorrowerPanCard: "borrowerPanCard",
            PartnershipDeed: "partnershipDeed",
            ListOfPatner: "listOfPatner",
            ITR: "itr",
            BalanceSheet: "balanceSheet",
            AadharCard: "AadharCard",
            PanCard: "panCard",
            BankStatement: "bankStatement",
            SalarySlips: "salarySlips",
            SLLoan: "slLoan",
            CompanyPAN: "borrowerPanCard",
            MOA: "moa",
            AOA: "aoa",
            ListOfDirector: "listOfDirector",
            Shareholding: "shareholding",
            CoApplicantITR: "coApplicantItr",
            ApplicantITR: "applicantItr",
            ApplicantPan: "applicantPan",
            ApplicantAadhar: "applicantAadhar",
            CoApplicantPan: "coApplicantPan",
            CoApplicantAadhar: "coApplicantAadhar",
            ApplicantForm16: "applicantForm16",
            CoApplicantForm16: "coApplicantForm16",
            GSTDocx: "gstDocx",
            AS26: "as26",
            "PAN Partner": "panCard",
            "Aadhar Partner": "aadharCard",
            "PAN Director": "panCard",
            "Aadhar Director": "AadharCard",
            "Aadhar Propreitor": "AadharCard",
            "PAN Propreitor": "panCard",
            OrderCopy: "orderCopy",
            ContractCopy: "contractCopy",
            Certification: "certification",
            CreditRating: "creditRating",
            JoiningLetter: "joiningLetter",
            IncorporationDateProof: "incorporationDateProof",
            DebitProfile: "debitProfile",
            ProvisionalBalanceSheet: "provisionalBalanceSheet",
            CompanyBrief: "companyBrief",
            CoLetteral: "coLetteral",
            "Other Relevant Docs": "other",
          };

          const sortedDocuments = [...applicationData.documents].sort(
            (a, b) => b.documentId - a.documentId
          );
          const documentsByType = {};
          sortedDocuments.forEach((doc) => {
            const docType = doc.documentType;
            if (!documentsByType[docType]) {
              documentsByType[docType] = doc;
            }
          });

          const arrayDocumentTypes = [
            "ITR",
            "BalanceSheet",
            "SalarySlips",
            "BankStatement",
            "SLLoan",
            "PanCard",
            "AadharCard",
            "CoLetteral",
            "Other Relevant Docs",
          ];

          const partnerDocumentTypes = new Set();
          sortedDocuments.forEach((doc) => {
            if (doc.documentType.includes("Partner") && doc.documentName && doc.documentName.match(/-\d+$/)) {
              const baseType = doc.documentType.split("-")[0].trim();
              partnerDocumentTypes.add(baseType);
            }
          });

          const directorDocumentTypes = new Set();
          sortedDocuments.forEach((doc) => {
            if (doc.documentType.includes("Director") && doc.documentName && doc.documentName.match(/-\d+$/)) {
              const baseType = doc.documentType.split("-")[0].trim();
              directorDocumentTypes.add(baseType);
            }
          });

          const proprietorDocumentTypes = new Set();
          sortedDocuments.forEach((doc) => {
            if ((doc.documentType.includes("Propreitor") || doc.documentType.includes("Proprietor")) && doc.documentName && doc.documentName.match(/-\d+$/)) {
              const baseType = doc.documentType.split("-")[0].trim();
              proprietorDocumentTypes.add(baseType);
            }
          });

          partnerDocumentTypes.forEach((type) => {
            if (!arrayDocumentTypes.includes(type)) arrayDocumentTypes.push(type);
          });
          directorDocumentTypes.forEach((type) => {
            if (!arrayDocumentTypes.includes(type)) arrayDocumentTypes.push(type);
          });
          proprietorDocumentTypes.forEach((type) => {
            if (!arrayDocumentTypes.includes(type)) arrayDocumentTypes.push(type);
          });

          const arrayDocuments = {};
          sortedDocuments.forEach((doc) => {
            const docType = doc.documentType;
            if (arrayDocumentTypes.includes(docType)) {
              if (!arrayDocuments[docType]) arrayDocuments[docType] = [];
              let index = 0;
              if (doc.documentName) {
                const match = doc.documentName.match(/\d+$/);
                if (match) index = parseInt(match[0]) - 1;
              }
              arrayDocuments[docType][index] = doc;
            } else if (
              doc.documentName &&
              doc.documentName.match(/-\d+$/) &&
              (doc.documentType.includes("Partner") || doc.documentType.includes("Director"))
            ) {
              const baseType = doc.documentType;
              if (!arrayDocuments[baseType]) arrayDocuments[baseType] = [];
              const match = doc.documentName.match(/-(\d+)$/);
              if (match) {
                const index = parseInt(match[1]) - 1;
                arrayDocuments[baseType][index] = doc;
              } else {
                arrayDocuments[baseType].push(doc);
              }
            } else if (
              doc.documentName &&
              doc.documentName.match(/-\d+$/) &&
              (doc.documentType.includes("Propreitor") || doc.documentType.includes("Proprietor"))
            ) {
              const baseType = doc.documentType;
              if (!arrayDocuments[baseType]) arrayDocuments[baseType] = [];
              const match = doc.documentName.match(/-(\d+)$/);
              if (match) {
                const index = parseInt(match[1]) - 1;
                arrayDocuments[baseType][index] = doc;
              } else {
                arrayDocuments[baseType].push(doc);
              }
            }
          });

          Object.entries(documentsByType).forEach(([docType, doc]) => {
            if (arrayDocumentTypes.includes(docType)) return;
            const fieldName = documentTypeMapping[docType] || docType.toLowerCase();
            filesObj[fieldName] = {
              filename: doc.documentName || docType,
              url: doc.documentPath,
              documentPath: doc.documentPath,
              remote: true,
              documentId: doc.documentId,
            };
          });

          Object.entries(arrayDocuments).forEach(([docType, docs]) => {
            const fieldName = documentTypeMapping[docType] || docType.toLowerCase();
            if (!fieldName) return;
            if (docs.length > 0) {
              filesObj[fieldName] = docs
                .map((doc) =>
                  doc
                    ? {
                        filename: doc.documentName || `${docType}`,
                        url: doc.documentPath,
                        documentPath: doc.documentPath,
                        documentId: doc.documentId,
                        remote: true,
                      }
                    : null
                )
                .filter(Boolean);
            }
          });

          dispatch(setFiles(filesObj));
        }

        dispatch(setResumed(true));
        navigate(`/apply-loan?resume=${applicationId}`, { state: { fromResumePage: true, fromApplicationHistory: true } });
      }
    } catch (err) {
      toast.error("Error fetching application details:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDocs = (application) => {
    // Navigate to upload documents
    navigate(`/document-upload?applicationId=${application.applicationId}`);
  };
  if (loading) {
    return (
      <PageWrapper>
        <ContentContainer
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          <CircularProgress />
        </ContentContainer>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <ContentContainer>
        <Typography
          variant="h4"
          sx={{
            color: "#0C53B7",
            fontFamily: "'Inter', sans-serif",
            fontWeight: 700,
            fontSize: "24px",
            mb: 1,
          }}
        >
          Application History
        </Typography>
        <Typography
          sx={{
            color: "#637381",
            mb: 3,
            fontFamily: "'Inter', sans-serif",
            fontSize: "16px",
            fontWeight: 400,
            fontStyle: "italic",
            lineHeight: "1.5",
          }}
        >
          Track the status of all your loan applications, from submission to
          approval.
        </Typography>

        {error && (
          <Typography
            sx={{
              color: "error.main",
              mb: 2,
              fontFamily: "'Inter', sans-serif",
              fontSize: "16px",
              fontWeight: 500,
            }}
          >
            {error}
          </Typography>
        )}

        {applications.length > 0 ? (
          <>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by Application Number, Loan Type, Status..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ 
                  maxWidth: 600,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#f8f9fa",
                    "&:hover": {
                      backgroundColor: "#f0f1f2",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "white",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <CentralizedTable
              data={applications}
              columns={borrowerApplicationColumns}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              totalRecords={applications.length}
              // Action handlers
              onDownloadDocument={handleDownloadDocument}
              onViewOffers={(row, offer) => handleViewOffer(offer)}
              onTrackApplication={handleTrackApplication}
              onViewApplication={handleViewApplication}
              onEditApplication={handleEditApplication}
              onUploadDocs={handleUploadDocs}
              // Configuration
              showReports={true}
              showOffers={true}
              showActions={true}
              useMaterialTable={true}
              hover={true}
              minWidth="650px"
              // Additional props
              pdfRefs={pdfRefs}
              onSort={handleSort}
              currentSortBy={sortBy}
              currentOrder={order}
            />
            {previewOpen && (
              <PreviewApplication
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                data={selectedApplication}
                readOnly={true}
              />
            )}
            {/* Hidden PDF components for each application */}
            {applications.map((application) => (
              <Box
                key={`pdf-${application.id}`}
                sx={{
                  position: "absolute",
                  left: "-9999px",
                  top: 0,
                  width: "794px",
                  height: "auto",
                  overflow: "visible",
                }}
              >
                <LoanApplicationPDF
                  ref={(el) => {
                    if (el) {
                      pdfRefs.current[application.id] = el;
                    }
                  }}
                  data={{ ...application, loanDoc: {}, userInfo: userFromRedux }}
                />
              </Box>
            ))}
          </>
        ) : (
          <EmptyStateContainer>
            <Box
              component="img"
              src="/No_Loan_Application.png"
              alt="No applications"
              sx={{
                width: "180px",
                height: "180px",
                marginBottom: "24px",
              }}
            />
            <Typography
              variant="h5"
              sx={{
                color: "#212B36",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                fontSize: "24px",
                mb: 1,
              }}
            >
              No loan applications found!
            </Typography>
            <Typography
              sx={{
                color: "#637381",
                fontFamily: "'Inter', sans-serif",
                fontSize: "16px",
                fontWeight: 400,
                fontStyle: "italic",
                mb: 2,
              }}
            >
              Start your journey by applying for a loan today.
            </Typography>
            <ApplyLoanButton onClick={handleApplyLoan}>
              Apply for a Loan
            </ApplyLoanButton>
          </EmptyStateContainer>
        )}
      </ContentContainer>
    </PageWrapper>
  );
};

export default ApplicationHistory;
